// Regroups one parsed 850 or 852 transaction by *kind of data* (order/report
// details, dates, parties, terms, line/activity items, notes, totals) rather
// than by X12 loop position, so the UI can show a short list of collapsible
// sections a reader recognizes. Each rendered segment keeps the exact
// finding-path base the validator uses (lib/validator.ts), so findings can
// be rolled up onto any collapsed section, party or line before the user
// expands it.

import type {EdiSegment} from './tokenizer';
import type {
  Header850,
  ItemLoop850,
  ItemNameSubLoop850,
  NameLoop850,
  Parsed850,
} from './parser850';
import type {
  Header852,
  ItemActivityLoop852,
  ItemNameSubLoop852,
  NameLoop852,
  Parsed852,
} from './parser852';
import type {EdiTransactionExplained} from './explainedEnvelope';
import type {ValidationFinding} from './validator';
import {transactionPathPrefix} from './segmentPaths';

export interface SectionEntry {
  segment: EdiSegment;
  /** Finding-path base, e.g. "groups[0].transactions[0].header.refs[1]". */
  basePath: string;
}

/** One collapsible unit inside a section — a single party (N1 loop) or a
 * single line (PO1/LIN). Sections that don't repeat use one untitled group. */
export interface SectionGroup {
  key: string;
  entries: SectionEntry[];
}

export type SectionKey =
  | 'order'
  | 'dates'
  | 'parties'
  | 'terms'
  | 'items'
  | 'notes'
  | 'totals';

export interface Section {
  key: SectionKey;
  title: string;
  description: string;
  /** Whether each group is its own collapsible row (parties, line items). */
  repeating: boolean;
  groups: SectionGroup[];
}

const SECTION_ORDER: SectionKey[] = [
  'order',
  'dates',
  'parties',
  'terms',
  'items',
  'notes',
  'totals',
];

/** Flattens a record of optional/repeated segments into pathed entries,
 * preserving the order keys are listed in. */
function entriesOf<T extends object>(
  source: T,
  keys: ReadonlyArray<keyof T & string>,
  basePath: string
): SectionEntry[] {
  const out: SectionEntry[] = [];
  for (const key of keys) {
    const value = source[key] as EdiSegment | EdiSegment[] | undefined;
    if (Array.isArray(value)) {
      value.forEach((segment, i) =>
        out.push({segment, basePath: `${basePath}.${key}[${i}]`})
      );
    } else if (value) {
      out.push({segment: value, basePath: `${basePath}.${key}`});
    }
  }
  return out;
}

export interface IssueCounts {
  errors: number;
  warnings: number;
}

/** Counts the findings that fall on or under any of the given segment base
 * paths — used to highlight a collapsed row before it's expanded. */
export function countIssues(
  findings: ValidationFinding[],
  basePaths: string[]
): IssueCounts {
  const counts: IssueCounts = {errors: 0, warnings: 0};
  for (const finding of findings) {
    const matches = basePaths.some(
      p => finding.path === p || finding.path.startsWith(`${p}.`)
    );
    if (!matches) continue;
    if (finding.severity === 'error') counts.errors++;
    else counts.warnings++;
  }
  return counts;
}

export function groupBasePaths(groups: SectionGroup[]): string[] {
  return groups.flatMap(g => g.entries.map(e => e.basePath));
}

// --- 850 ---------------------------------------------------------------

type HeaderKey850 = keyof Header850;

/** Which data-type section each 850 header-level segment belongs to. */
const HEADER_SECTION_850: Record<HeaderKey850, SectionKey> = {
  beg: 'order',
  cur: 'order',
  refs: 'order',
  per: 'order',
  pwk: 'order',
  lm: 'order',
  lq: 'order',
  dtm: 'dates',
  ldt: 'dates',
  tax: 'terms',
  fob: 'terms',
  ctp: 'terms',
  itd: 'terms',
  dis: 'terms',
  inc: 'terms',
  pct: 'terms',
  iss: 'terms',
  msg: 'notes',
};

const SECTION_META_850: Record<
  SectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Order details',
    description:
      'PO number, purpose, currency, reference numbers and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'Requested, promised and other order dates.',
  },
  parties: {
    title: 'Parties & addresses',
    description:
      'Who is involved — ship to, bill to, buyer, vendor — and where.',
  },
  terms: {
    title: 'Pricing, terms & shipping',
    description: 'Payment terms, tax, pricing, FOB and shipping instructions.',
  },
  items: {
    title: 'Line items',
    description: 'Each ordered product, quantity and price.',
  },
  notes: {title: 'Notes', description: 'Free-form messages on the order.'},
  totals: {title: 'Totals', description: 'Line-item count and order totals.'},
};

const NAME_LOOP_KEYS_850: ReadonlyArray<keyof NameLoop850> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
];
const ITEM_KEYS_850: ReadonlyArray<
  Exclude<keyof ItemLoop850, 'sln' | 'transportation' | 'subNameLoops'>
> = [
  'po1',
  'pid',
  'po3',
  'ctp',
  'mea',
  'pwk',
  'pkg',
  'refs',
  'per',
  'sac',
  'it8',
  'ctb',
  'tax',
  'fob',
  'sdq',
  'dtm',
  'ldt',
  'iss',
  'pam',
  'msg',
  'schedule',
  'csh',
  'tc2',
  'pct',
];
const ITEM_NAME_KEYS_850: ReadonlyArray<keyof ItemNameSubLoop850> = [
  'n1',
  'n2',
  'n3',
  'n4',
];

/** Builds the data-type sections for one 850 transaction, omitting any
 * section with nothing in it. */
function buildSections850(parsed: Parsed850, txPath: string): Section[] {
  const flat: Record<SectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_850) as HeaderKey850[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_850[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_850, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_850, path);
    if (item.sln)
      entries.push({segment: item.sln.sln, basePath: `${path}.sln.sln`});
    if (item.transportation) {
      entries.push({
        segment: item.transportation.tc1,
        basePath: `${path}.transportation.tc1`,
      });
    }
    item.subNameLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.subNameLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_850, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_850[key], repeating, groups}];
  });
}

// --- 852 ---------------------------------------------------------------

type HeaderKey852 = keyof Header852;

/** Which data-type section each 852 header-level segment belongs to. */
const HEADER_SECTION_852: Record<HeaderKey852, SectionKey> = {
  bct: 'order',
  refs: 'order',
  per: 'order',
  pwk: 'order',
  dtm: 'dates',
  fob: 'terms',
  msg: 'notes',
};

const SECTION_META_852: Record<
  SectionKey,
  {title: string; description: string}
> = {
  order: {
    title: 'Report details',
    description: 'Report reference number, activity type and contacts.',
  },
  dates: {
    title: 'Dates',
    description: 'The report period and other reported dates.',
  },
  parties: {
    title: 'Parties & locations',
    description:
      'Who this report is about — the reporting retailer/distributor — and where.',
  },
  terms: {
    title: 'Shipping',
    description: 'F.O.B. and shipping-related instructions.',
  },
  items: {
    title: 'Activity lines',
    description:
      'Each reported item and its quantities — units sold, on hand, on order, received.',
  },
  notes: {title: 'Notes', description: 'Free-form messages on the report.'},
  totals: {
    title: 'Totals',
    description: 'Line-activity count and report totals.',
  },
};

const NAME_LOOP_KEYS_852: ReadonlyArray<keyof NameLoop852> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
];
const ITEM_KEYS_852: ReadonlyArray<
  Exclude<keyof ItemActivityLoop852, 'subNameLoops'>
> = ['lin', 'pid', 'ctp', 'sdq', 'qty', 'refs', 'per', 'dtm'];
const ITEM_NAME_KEYS_852: ReadonlyArray<keyof ItemNameSubLoop852> = [
  'n1',
  'n2',
  'n3',
  'n4',
];

/** Builds the data-type sections for one 852 transaction, omitting any
 * section with nothing in it. */
function buildSections852(parsed: Parsed852, txPath: string): Section[] {
  const flat: Record<SectionKey, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_852) as HeaderKey852[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_852[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_852, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    const entries = entriesOf(item, ITEM_KEYS_852, path);
    item.subNameLoops.forEach((sub, subIndex) => {
      const subPath = `${path}.subNameLoops[${subIndex}]`;
      entries.push(...entriesOf(sub, ITEM_NAME_KEYS_852, subPath));
    });
    return {key: path, entries};
  });

  return SECTION_ORDER.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_852[key], repeating, groups}];
  });
}

// --- dispatcher ----------------------------------------------------------

/** Builds the data-type sections for one explained transaction — 850 or 852,
 * whichever of `parsed850`/`parsed852` is present. A transaction that's
 * neither (shown read-only) yields no sections. */
export function buildSections(
  transaction: EdiTransactionExplained,
  groupIndex: number,
  transactionIndex: number
): Section[] {
  const txPath = transactionPathPrefix(groupIndex, transactionIndex);
  if (transaction.parsed850)
    return buildSections850(transaction.parsed850, txPath);
  if (transaction.parsed852)
    return buildSections852(transaction.parsed852, txPath);
  return [];
}
